@if (!readOnly()) {
    <div class="flex items-center justify-between gap-3 mb-4">
        <p class="text-sm text-p-text-muted m-0">{{ 'bookingV2.advanced.documents.hint' | translate }}</p>
        <div class="flex items-center gap-2">
            @if (canCreateContract()) {
                <p-button type="button" severity="secondary" [outlined]="true" (onClick)="openContractDialog()">
                    <span pButtonIcon class="material-icons" style="font-size: 1.125rem">description</span>
                    <span pButtonLabel>{{ 'bookingV2.contract.createButton' | translate }}</span>
                </p-button>
            }
            <p-button type="button" (onClick)="toggleForm()">
                <span pButtonIcon class="material-icons" style="font-size: 1.125rem">upload</span>
                <span pButtonLabel>{{ 'bookingV2.advanced.documents.uploadButton' | translate }}</span>
            </p-button>
        </div>
    </div>
}

@if (contractDialogOpen() && contractPerson(); as person) {
    <booking-v2-contract-dialog
        [visible]="true"
        (visibleChange)="onContractVisibleChange($event)"
        [bookingId]="contractBookingId()"
        [person]="person"
        [boat]="contractBoat()"
        [berths]="contractBerths()"
        [fromDate]="contractFrom()"
        [toDate]="contractTo()"
        (created)="onContractCreated()" />
}

@if (showForm()) {
    <div class="bg-p-surface-50 border border-dashed border-p-border rounded-lg p-5 mb-4 flex flex-col gap-3">
        <h4 class="font-semibold text-p-text text-sm m-0">{{ 'bookingV2.advanced.documents.formTitle' | translate }}</h4>

        <div class="flex flex-col gap-1">
            <label for="doc-name" class="text-sm font-medium text-p-text">
                {{ 'bookingV2.advanced.documents.name' | translate }}
                <span class="text-red-500" aria-hidden="true">*</span>
            </label>
            <input pInputText id="doc-name" type="text" class="w-full"
                [placeholder]="('bookingV2.advanced.documents.namePlaceholder' | translate)"
                [ngModel]="name()" (ngModelChange)="name.set($event)" />
        </div>

        @if (showCategory()) {
            <div class="flex flex-col gap-1">
                <label for="doc-category" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.advanced.documents.category' | translate }}
                    <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p-select inputId="doc-category" styleClass="w-full" appendTo="body"
                    optionLabel="name" dataKey="id" [filter]="true" filterBy="name"
                    [options]="categoryOptions() ?? []" [ngModel]="category()" (ngModelChange)="category.set($event)"
                    [placeholder]="('bookingV2.advanced.documents.categoryPlaceholder' | translate)">
                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                    <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                </p-select>
            </div>
        }

        <!-- Real file picker: hidden input triggered by the dropzone button -->
        <input #fileInput type="file" class="hidden" (change)="onFileSelected($event)" />
        <button type="button"
            class="border-2 border-dashed border-p-border rounded-lg p-6 text-center bg-p-content-bg w-full cursor-pointer hover:bg-p-surface-50 transition-colors"
            [disabled]="uploading()"
            (click)="fileInput.click()">
            @if (selectedFile(); as file) {
                <span class="material-icons text-p-text" style="font-size: 1.5rem;" aria-hidden="true">description</span>
                <p class="text-sm text-p-text mt-2 m-0 font-medium truncate">{{ file.name }}</p>
                <p class="text-xs text-p-text-muted mt-1 m-0">
                    {{ formatBytes(file.size) }} · {{ 'bookingV2.advanced.documents.changeFile' | translate }}
                </p>
            } @else {
                <span class="material-icons text-p-text-muted" style="font-size: 1.5rem;" aria-hidden="true">attach_file</span>
                <p class="text-sm text-p-text-muted mt-2 m-0">{{ 'bookingV2.advanced.documents.dropzone' | translate }}</p>
                <p class="text-xs text-p-text-muted mt-1 m-0">{{ 'bookingV2.advanced.documents.dropzoneHint' | translate }}</p>
            }
        </button>

        <div class="flex items-center gap-2 justify-end">
            <p-button type="button" [disabled]="!canAdd()" [label]="'bookingV2.advanced.documents.add' | translate" (onClick)="addDocument()" />
            <p-button type="button" severity="secondary" [text]="true" [disabled]="uploading()" [label]="'common.cancel' | translate" (onClick)="closeForm()" />
            @if (uploading()) {
                <span class="flex items-center gap-2 text-sm text-p-text-muted" role="status" aria-live="polite">
                    <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                    {{ 'bookingV2.advanced.documents.uploading' | translate }}
                </span>
            }
        </div>
    </div>
}

<div class="flex flex-col gap-2">
    @for (doc of documents(); track doc.id) {
        <div class="bg-p-content-bg border border-p-border rounded-lg p-4 flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
                <span class="material-icons flex-shrink-0 text-p-text-muted" aria-hidden="true">description</span>
                <div class="min-w-0">
                    <p class="font-semibold text-p-text m-0">{{ doc.name }}</p>
                    <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                        @if (doc.categoryName) {
                            <span class="px-2 py-0.5 bg-sky-100 text-sky-800 rounded-full text-xs font-medium">{{ doc.categoryName }}</span>
                        }
                        <span class="text-xs text-p-text-muted truncate">{{ doc.fileName }}</span>
                        @if (doc.size) { <span class="text-xs text-p-text-muted">{{ formatBytes(doc.size) }}</span> }
                        <span class="text-xs text-p-text-muted inline-flex items-center gap-0.5">
                            <span class="material-icons text-xs" aria-hidden="true">event</span>
                            {{ doc.uploadDate | date:'dd.MM.yyyy' }}
                        </span>
                    </div>
                </div>
            </div>
            @if (!readOnly() && (doc.documentId == null || canDeleteDocuments)) {
                <u2b-icon-button
                    icon="delete_outline"
                    [ariaLabel]="('bookingV2.advanced.documents.removeAria' | translate)"
                    (clicked)="removeDocument(doc.id)" />
            }
        </div>
    }
    @if (documents().length === 0 && !showForm()) {
        <div class="text-center py-10">
            <span class="material-icons text-p-surface-300" style="font-size: 2.25rem;" aria-hidden="true">description</span>
            <p class="text-p-text-muted text-sm mt-3 m-0">{{ 'bookingV2.advanced.documents.empty' | translate }}</p>
        </div>
    }
</div>
